"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Service, ServicesTab, ServiceLink } from "@/types/data";
import useEmblaCarousel from "embla-carousel-react";
import { gsap } from "@/lib/gsap";
import { ServicesTabsHeader } from "./ServicesTabsHeader";

interface ServicesTabsProps {
  services: Service[];
  servicesTabs: ServicesTab[];
  links?: ServiceLink[];
}

export function ServicesTabs({
  services,
  servicesTabs,
  links,
}: ServicesTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  const safeActiveTab =
    services && services.length > 0 && activeTab < services.length
      ? activeTab
      : 0;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    direction: "ltr",
    containScroll: "trimSnaps",
    skipSnaps: false,
  });

  const handleTabSwitch = (index: number) => {
    setActiveTab(index);
  };

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!emblaApi || !isMounted) return;

    const container = emblaApi.containerNode();
    if (container) {
      container.style.transition = "transform 0.5s ease-out";
    }
  }, [emblaApi, isMounted]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (emblaApi && isMounted) {
      emblaApi.scrollTo(activeTab, false);
    }
  }, [activeTab, emblaApi, isMounted]);

  useEffect(() => {
    if (!emblaApi || !isMounted) return;

    const onSelect = () => {
      const selectedIndex = emblaApi.selectedScrollSnap();
      if (selectedIndex !== activeTab) {
        setActiveTab(selectedIndex);
      }
    };

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, isMounted, activeTab]);

  useEffect(() => {
    if (!headerRef.current || !isMounted) return;

    const ctx = gsap.context(() => {
      const elements = headerRef.current?.querySelectorAll("[data-animate]");

      if (elements) {
        gsap.fromTo(
          elements,
          {
            opacity: 0,
            y: 30,
            filter: "blur(8px)",
          },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
              once: true,
            },
          }
        );
      }
    }, headerRef);

    return () => ctx.revert();
  }, [isMounted]);

  return (
    <>
      {}
      <ServicesTabsHeader
        servicesTabs={servicesTabs}
        activeTab={safeActiveTab}
        onTabSwitch={handleTabSwitch}
      />

      {}
      <div
        ref={headerRef}
        className={`flex flex-col lg:flex-row items-start lg:items-center mt-12 ${
          isMounted ? "" : "opacity-0"
        }`}
      >
        {}
        <div className="lg:w-1/3 w-full  relative">
          <div>
            {services && services[safeActiveTab] && (
              <>
                <h2
                  data-animate
                  className=" text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight lg:leading-[43.2px] text-theme-secondary"
                >
                  {services[safeActiveTab].title}
                </h2>
                <p
                  data-animate
                  className=" mt-4 text-[#4F5051] text-base sm:text-lg lg:text-[20px] leading-relaxed lg:leading-7 font-medium"
                >
                  {services[safeActiveTab].description}
                </p>
                {services[safeActiveTab].link && (
                  <Link
                    data-animate
                    href={`/services/${services[safeActiveTab].link.url}`}
                    className="btn btn-primary btn-primary-hover-effect mt-6 cursor-pointer transition-all duration-300 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%]"
                  >
                    <span className="relative z-10 transition-all duration-300 text-white group-hover:text-white">
                      {services[safeActiveTab].link.text}
                    </span>
                    <span className="sr-only"> {services[safeActiveTab].link.text} </span>
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {}
        <div
          data-animate
          className={`lg:w-2/3 w-full lg:pl-12 max-lg:pt-8 ${
            isMounted ? "" : "opacity-0"
          }`}
        >
          <div
            className="overflow-hidden h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px]"
            ref={emblaRef}
          >
            <div className="flex gap-4 sm:gap-6 md:gap-8 lg:gap-12 pl-4 pr-4 sm:pl-6 sm:pr-6 md:pl-8 md:pr-8 lg:pl-12 lg:pr-12">
              {links &&
                links.length > 0 &&
                links.map(
                  (
                    {
                      slug,
                      image,
                      imageAlt,
                    }: { slug: string; image: string; imageAlt: string },
                    index: number
                  ) => (
                    <div key={index} className="flex-[0_0_auto] min-w-0 group">
                      <div className="relative w-[325px] h-[200px] sm:w-[400px] sm:h-[250px] md:w-[450px] md:h-[300px] lg:w-[500px] lg:h-[350px] overflow-hidden rounded-xl">
                        <Link href={`/services/${slug}`}>
                          {image && (
                            <Image
                              src={image}
                              alt={imageAlt}
                              width={500}
                              height={350}
                              quality={85}
                              sizes="(max-width: 768px) 325px, (max-width: 1024px) 400px, (max-width: 1200px) 450px, 500px"
                              className="!size-full object-cover rounded-xl cursor-pointer transition-all duration-500 grayscale-[0.3] group-hover:scale-110 group-hover:grayscale-0 group-hover:brightness-105"
                            />
                          )}
                        </Link>
                        {}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-[#F97316]/50 via-[#F6842B]/40 to-[#2563EB]/50 mix-blend-overlay"></div>

                        {}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300 pointer-events-none">
                          <Link
                            href={`/services/${slug}`}
                            className="relative w-16 h-16 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-white/20 ring-1 ring-white/40 shadow-[0_8px_30px_rgba(0,0,0,0.3)]  hover:bg-white/30 transition-all duration-300"
                          >
                            <span className="relative z-10 flex items-center justify-center text-center">
                              Read More
                              <span className="sr-only"> {services[safeActiveTab].link.text} </span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
